import React, { useEffect, useState } from 'react';
import {
  ButtonTertiary,
  cssColorValue,
  IconChevronDown,
  IconChevronUp,
  screenSmMin,
  semanticColors,
  spacer12,
  spacer16,
  spacer24,
  spacer32,
  spacer8,
  Type,
} from '@spotify-internal/encore-web';
import styled, { css } from 'styled-components';

import useMobileView from 'experiments/feedback-position/hooks/useMobileView';
import useIsHelpfulAnswer from 'experiments/feedback-position/hooks/useIsHelpfulAnswer';
import useSubmitReason from 'experiments/feedback-position/hooks/useSubmitReason';
import useTrackMessageUsLink from 'experiments/feedback-position/hooks/useTrackMessageUsLink';
import { useTranslation } from 'providers/translations/TranslationProvider';
import { UbiArticlePage } from 'utils/ubi/pages/useUbiArticle';

import FeedbackWidgetContent from './FeedbackWidgetContent';

const StyledContainer = styled.div`
  border-top: 2px solid ${cssColorValue(semanticColors.decorativeSubdued)};
  padding: ${spacer8};
  padding-top: ${spacer32};
  margin-block-start: ${spacer8};

  @media (max-width: ${screenSmMin}) {
    padding-top: ${spacer16};
  }
`;

const StyledSticky = styled.div<{ isSticky?: boolean }>`
  padding: ${spacer24} 0;
  ${props =>
    props.isSticky &&
    css`
      position: sticky;
      background-color: var(--background-base);
      bottom: 0;
    `};

  @media (max-width: ${screenSmMin}) {
    padding: ${spacer12};
  }
`;

const StyledRow = styled.div`
  display: flex;
  flex-direction: row;
  column-gap: ${spacer8};
  align-content: flex-start;
  justify-content: space-between;
`;

interface FeedbackWidgetProps {
  articleIdentifier: string;
  ubi: UbiArticlePage;
  isSticky?: boolean; // we will remove the sticky property when it is POPUP
}
const FeedbackWidget = ({ articleIdentifier, ubi, isSticky }: FeedbackWidgetProps) => {
  const { t } = useTranslation();

  const [open, setOpen] = useState(true);

  const isMobileView = useMobileView();
  const isStickyMobile = isSticky && isMobileView;

  const { isHelpfulAnswered, answerIfIsHelpful } = useIsHelpfulAnswer({ ubi, articleIdentifier });
  const { isSubmittedReason, submitReason } = useSubmitReason({ ubi, articleIdentifier });
  const { trackMessageUsLink } = useTrackMessageUsLink({ ubi, articleIdentifier });

  useEffect(() => {
    ubi.logArticleHelpfulImpression();
  }, [ubi]);

  return (
    <StyledSticky isSticky={isSticky}>
      <StyledContainer>
        <StyledRow>
          <Type as="p" variant="balladBold" paddingBottom="1em">
            {t('feedback_widget_was_this_helpful')}
          </Type>
          {isStickyMobile && (
            <ButtonTertiary
              buttonSize={ButtonTertiary.sm}
              onClick={() => setOpen(!open)}
              cellPadding={0}
              data-testid="mobile-collapse-button"
            >
              {!open ? <IconChevronDown /> : <IconChevronUp />}
            </ButtonTertiary>
          )}
        </StyledRow>
        {(!isStickyMobile || open) && (
          <FeedbackWidgetContent
            isHelpfulAnswered={isHelpfulAnswered}
            answerIfIsHelpful={answerIfIsHelpful}
            isSubmittedReason={isSubmittedReason}
            submitReason={submitReason}
            trackMessageUsLink={trackMessageUsLink}
          />
        )}
      </StyledContainer>
    </StyledSticky>
  );
};

export default FeedbackWidget;
